import {
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  Button,
  Drawer,
  toast,
} from '@ui/components';
import {
  ErrorDetailsDtoErrorCodeEnum,
  EmployeeTableDataDto,
  EditEmployeeRequest,
} from '@interfaces/models';
import { formatPhoneNumberToDisplay, sanitizePhoneNumber } from '@helpers/utils/phone-number';
import { useOnboardingImportedEmployees } from '@pages/onboarding/hooks';
import { Cancel, Delete } from '@ui/components/icons';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

import {
  useEditEmployeesDrawerForm,
  EditEmployeesForm,
} from './edit-employee-drawer-form/use-edit-employee-drawer-form';
import { EditEmployeesDrawerForm } from './edit-employee-drawer-form/edit-employee-drawer-form';

export const EditEmployeeDrawer = ({
  showRemoveEmployeeDialog,
  onClose,
  isOpen,
  data,
}: {
  onClose: ({ hasBeenModified }?: { hasBeenModified: boolean }) => void;
  showRemoveEmployeeDialog: () => void;
  data: EmployeeTableDataDto;
  isOpen: boolean;
}) => {
  const { t } = useTranslation();

  const { form } = useEditEmployeesDrawerForm();

  const { editEmployee, isEditEmployeePending } = useOnboardingImportedEmployees({
    onEditEmployeeSuccess: () => {
      onClose({
        hasBeenModified: true,
      });

      toast.success(t('ONBOARDING.IMPORT_EMPLOYEES.EMPLOYEE_EDITED_SUCCESSFULLY'));
    },
    onEditEmployeeError: (error: ErrorDetailsDtoErrorCodeEnum) => {
      if (error === ErrorDetailsDtoErrorCodeEnum.USER_EDIT_EMPLOYEE_ALREADY_EXISTS) {
        form.setError('phoneNumber', {
          message: t('API_ERRORS.USER_EDIT_EMPLOYEE_ALREADY_EXISTS'),
        });
      } else {
        toast.error(t(`API_ERRORS.DEFAULT`));
      }
    },
  });

  const handleEditEmployee = (employee: EditEmployeeRequest) => {
    const payload: EditEmployeeRequest = {
      ...employee,
      phoneNumber: sanitizePhoneNumber(employee.phoneNumber),
    };

    editEmployee({ employee: payload });
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  // set form values
  useEffect(() => {
    const formValues: EditEmployeesForm = {
      firstName: data.firstName,
      lastName: data.lastName,
      phoneNumber: formatPhoneNumberToDisplay(data.phoneNumber ?? ''),
      phoneExt: data.phoneExt ?? '',
      email: data.email ?? '',
      labels: data.labels?.map((label) => label.id) ?? [],
      assignedVehicles: data.assignedVehicles?.map((vehicle) => vehicle.id) ?? [],
    };

    form.reset(formValues);
  }, [data, form]);

  return (
    <Drawer onOpenChange={handleClose} direction="right" open={isOpen} handleOnly>
      <DrawerContent>
        <DrawerHeader className="flex">
          <DrawerTitle>{t('ONBOARDING.IMPORT_EMPLOYEES.EDIT_EMPLOYEE_DRAWER_TITLE')}</DrawerTitle>
          <div className="flex ml-auto">
            <Button onClick={showRemoveEmployeeDialog} variant="ghost" type="button">
              <Delete />
            </Button>
            <Button onClick={handleClose} variant="ghost" type="button">
              <Cancel />
            </Button>
          </div>
        </DrawerHeader>
        <div className="px-4">
          <EditEmployeesDrawerForm form={form} />
        </div>
        <DrawerFooter>
          <Button
            onClick={form.handleSubmit((element) => {
              handleEditEmployee({ ...element, id: data.id });
            })}
            isLoading={isEditEmployeePending}
            disabled={isEditEmployeePending}
            size="large"
            wide
          >
            {t('ONBOARDING.IMPORT_EMPLOYEES.EDIT_EMPLOYEE_DRAWER_SAVE_CHANGES')}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
